import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DS } from '../PatientForms/designTokens';
import FormTextInput from './FormTextInput';
import { FormRadioGroup } from './FormRadioGroup';
import { headacheDaysWindow } from './formValidation';
import {
  DiaryState,
  diaryAnswer,
  headacheDaysView,
  isPlainWithoutDiary,
  isStaleAnswer,
  plainAnswer,
  plainHelperText,
  totalHelperText,
  unloggedDays,
} from './headacheDaysAnswer';
import { DiarySummary, HeadacheDaysAnswer, UnloggedAnswer } from './types';

const labelSx = {
  display: 'block',
  fontSize: '16px',
  fontWeight: 600,
  color: '#374151',
  mb: 1.5,
  fontFamily: DS.fontBody,
};

const smallTextSx = {
  fontSize: '13px',
  color: DS.inkSecondary,
  fontFamily: DS.fontBody,
};

const cardSx = {
  backgroundColor: DS.surfaceRaised,
  borderRadius: DS.radiusLg,
  boxShadow: DS.shadowSm,
  p: 2.5,
  mb: 2.5,
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
};

const LoadingCard: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box sx={cardSx} aria-busy="true">
      <Box
        sx={{
          width: '60%',
          height: 28,
          borderRadius: '999px',
          backgroundColor: DS.surfaceTint,
        }}
      />
      <Box
        sx={{
          height: 10,
          borderRadius: '999px',
          backgroundColor: DS.surfaceTint,
        }}
      />
      <Typography sx={smallTextSx}>
        {t('headacheDaysQuestion.loadingDiary')}
      </Typography>
    </Box>
  );
};

const DiaryCard: React.FC<{ summary: DiarySummary }> = ({ summary }) => {
  const { t } = useTranslation();
  const segments = [
    {
      label: t('headacheDaysQuestion.legendHeadache'),
      count: summary.headacheDays,
      color: DS.deepTeal,
      border: 'none',
    },
    {
      label: t('headacheDaysQuestion.legendNoHeadache'),
      count: summary.loggedDays - summary.headacheDays,
      color: DS.coolMint,
      border: 'none',
    },
    {
      label: t('headacheDaysQuestion.legendUnlogged'),
      count: unloggedDays(summary),
      color: DS.surfaceTint,
      border: `1px solid ${DS.hairline}`,
    },
  ];

  return (
    <Box sx={cardSx}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: DS.fontDisplay,
              fontWeight: 700,
              fontSize: '40px',
              lineHeight: 1,
              color: DS.deepTeal,
            }}
          >
            {summary.headacheDays}
          </Typography>
          <Typography sx={{ fontFamily: DS.fontBody, fontSize: '16px' }}>
            {t('headacheDaysQuestion.diaryHeadacheDays', {
              count: summary.headacheDays,
            })}
          </Typography>
        </Box>
        <Typography sx={{ ...smallTextSx, mt: 1 }}>
          {t('headacheDaysQuestion.loggedOfWindow', {
            logged: summary.loggedDays,
            window: summary.windowDays,
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '2px',
          height: 10,
          borderRadius: '999px',
          overflow: 'hidden',
        }}
      >
        {segments
          .filter(({ count }) => count > 0)
          .map(({ label, count, color }) => (
            <Box key={label} sx={{ flexGrow: count, backgroundColor: color }} />
          ))}
      </Box>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: '6px' }}
      >
        {segments.map(({ label, count, color, border }) => (
          <Box
            key={label}
            sx={{
              ...smallTextSx,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: color,
                border,
                boxSizing: 'border-box',
              }}
            />
            {label} {count}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface Props {
  value?: HeadacheDaysAnswer;
  diary: DiaryState;
  onChange: (value?: HeadacheDaysAnswer) => void;
  onBlur: () => void;
}

const HeadacheDaysQuestion: React.FC<Props> = ({
  value,
  diary,
  onChange,
  onBlur,
}) => {
  const { t } = useTranslation();
  const [keptPlainAnswer] = useState(() =>
    isPlainWithoutDiary(value) ? value : undefined
  );
  const view = keptPlainAnswer ? 'plain' : headacheDaysView(diary);
  const [diaryChanged, setDiaryChanged] = useState(false);
  const saveAfterChange = useRef(false);
  const [plainInput, setPlainInput] = useState(
    value?.headacheDaysTotal?.toString() ?? ''
  );
  const [totalInput, setTotalInput] = useState(
    value?.unloggedAnswer === 'yes' && value.headacheDaysTotal !== undefined
      ? String(value.headacheDaysTotal)
      : ''
  );

  useEffect(() => {
    if (!saveAfterChange.current) return;
    saveAfterChange.current = false;
    onBlur();
  }, [value, onBlur]);

  useEffect(() => {
    if (typeof diary !== 'object') return;
    if (value && isStaleAnswer(value, diary)) {
      setDiaryChanged(true);
      setPlainInput('');
      setTotalInput('');
      saveAfterChange.current = true;
      onChange(undefined);
      return;
    }
    if (!value && view === 'full') {
      saveAfterChange.current = diaryChanged;
      onChange(diaryAnswer(diary));
    }
  }, [diary, value, view, diaryChanged, onChange]);

  const notice = diaryChanged && (view === 'full' || !value) && (
    <Box
      sx={{
        ...smallTextSx,
        fontSize: '14px',
        backgroundColor: DS.coolMint,
        borderRadius: '12px',
        px: 2,
        py: 1.5,
        mb: 2,
      }}
    >
      {view === 'full'
        ? t('headacheDaysQuestion.diaryChangedAnswered')
        : t('headacheDaysQuestion.diaryChanged')}
    </Box>
  );

  if (view === 'plain' || view === 'sparse') {
    const windowDays =
      typeof diary === 'object' ? diary.windowDays : headacheDaysWindow();
    return (
      <Box id="q-headacheDaysLast3Months" sx={{ mb: 2 }}>
        {notice}
        <FormTextInput
          label={t('headacheDaysQuestion.question')}
          type="number"
          inputMode="numeric"
          min={0}
          max={windowDays}
          value={plainInput}
          onChange={(e) => {
            const field = e.target;
            const { value: input, validity } = field;
            if (validity.badInput) {
              field.value = plainInput;
              return;
            }
            setPlainInput(input);
            if (
              input === '' ||
              plainHelperText(input, windowDays, t) !== null
            ) {
              onChange(undefined);
              return;
            }
            if (keptPlainAnswer) {
              onChange({
                ...keptPlainAnswer,
                headacheDaysTotal: Number(input),
              });
              return;
            }
            onChange(plainAnswer(Number(input), windowDays, diary));
          }}
          onBlur={onBlur}
        />
        {plainHelperText(plainInput, windowDays, t) && (
          <Typography sx={{ ...smallTextSx, fontSize: '14px', mt: -1, mb: 2 }}>
            {plainHelperText(plainInput, windowDays, t)}
          </Typography>
        )}
        {view === 'sparse' && typeof diary === 'object' && (
          <Typography sx={{ ...smallTextSx, mt: -1 }}>
            {t('headacheDaysQuestion.sparseHint', {
              count: diary.headacheDays,
              logged: diary.loggedDays,
              window: diary.windowDays,
            })}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box id="q-headacheDaysLast3Months" sx={{ mb: 3 }}>
      <Typography sx={labelSx}>{t('headacheDaysQuestion.question')}</Typography>
      {notice}
      {typeof diary === 'object' ? (
        <DiaryCard summary={diary} />
      ) : (
        <LoadingCard />
      )}
      {view === 'diary' && typeof diary === 'object' && (
        <>
          <FormRadioGroup<UnloggedAnswer>
            label={t('headacheDaysQuestion.unloggedQuestion', {
              count: unloggedDays(diary),
            })}
            name="unloggedAnswer"
            value={value?.unloggedAnswer}
            options={[
              {
                label: t('headacheDaysQuestion.unloggedNo', {
                  count: diary.headacheDays,
                }),
                value: 'no',
              },
              {
                label: t('headacheDaysQuestion.unloggedYes', {
                  count: unloggedDays(diary),
                }),
                value: 'yes',
              },
              {
                label: t('headacheDaysQuestion.unloggedUnsure'),
                value: 'unsure',
              },
            ]}
            onChange={(choice) => {
              const total =
                choice === 'yes' &&
                totalInput !== '' &&
                totalHelperText(totalInput, diary, t) === null
                  ? Number(totalInput)
                  : undefined;
              onChange(diaryAnswer(diary, choice, total));
            }}
            onBlur={onBlur}
          />
          {value?.unloggedAnswer === 'yes' && (
            <>
              <FormTextInput
                labelFontWeight={400}
                labelFontSize="14px"
                label={t('headacheDaysQuestion.totalQuestion', {
                  count: diary.headacheDays,
                })}
                type="number"
                inputMode="numeric"
                min={diary.headacheDays + 1}
                max={diary.headacheDays + unloggedDays(diary)}
                value={totalInput}
                onChange={(e) => {
                  const field = e.target;
                  const { value: input, validity } = field;
                  if (validity.badInput) {
                    field.value = totalInput;
                    return;
                  }
                  setTotalInput(input);
                  const valid =
                    input !== '' && totalHelperText(input, diary, t) === null;
                  onChange(
                    diaryAnswer(diary, 'yes', valid ? Number(input) : undefined)
                  );
                }}
                onBlur={onBlur}
              />
              {totalHelperText(totalInput, diary, t) && (
                <Typography
                  sx={{ ...smallTextSx, fontSize: '14px', mt: -1, mb: 2 }}
                >
                  {totalHelperText(totalInput, diary, t)}
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default HeadacheDaysQuestion;
